// Login.js
import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../api/axios";
import { useNavigate, useLocation } from "react-router-dom";
import "./Login.css";

export default function Login() {
  const [input, setInput] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState("IDENTIFIER"); // IDENTIFIER | PASSWORD
  const [openRolePopup, setOpenRolePopup] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const buildLoginPayload = () => {
    const isEmail = input.includes("@");
    const userLocation = localStorage.getItem("user_location")
      ? JSON.parse(localStorage.getItem("user_location"))
      : {};

    return {
      username: input,
      email: isEmail ? input : "",
      mobile: !isEmail ? input : "",
      loginLocation: {
        city: userLocation.city || "Unknown City",
        state: userLocation.state || "Unknown State",
        country: userLocation.country || "Unknown Country",
        fullAddress: userLocation.full_address || "Unknown Address",
      },
    };
  };

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    // console.log("Login attempt with input:", input, "and step:", step);
    if (!input.trim()) return alert("Enter email or phone");

    try {
      /* =========================
         STEP 2: VERIFY PASSWORD
         ========================= */
      if (step === "PASSWORD") {
        const res = await api.post("/auth/verify-password", {
          username: input,
          password,
        }); 

        localStorage.setItem("token", res.data.token);
        localStorage.setItem("user", JSON.stringify(res.data.user));

        navigate(location.state?.redirectTo || "/");
        return;
      }

      /* =========================
         STEP 1: IDENTIFIER CHECK
         ========================= */
      const res = await api.post("/auth/login", buildLoginPayload());
      // console.log("Login response:", res.data);
      // 🔐 Existing user → ask password
      if (res.data.requiresPassword) {
        // console.log("Password required for user:", input);
        setStep("PASSWORD");
        return;
      }

      // 🆕 Existing user without password → role-based profile page
      if (res.data.requiresPasswordSetup) {
        const user = res.data.user;
        
        // Add registration flag for users who need to set password
        user.isRegistration = true;

        // console.log("Redirecting to profile setup for user:", user);
        localStorage.setItem("user", JSON.stringify(user));
        
        // 🔑 Store token if provided (for OTP verification during registration)
        if (res.data.token) {
          localStorage.setItem("token", res.data.token);
        }

        // 🔁 role-based redirect
        navigate(`/register/${user.role}`);
        return;
      }

      // Edge case: logged in directly
      // console.log("Edge Case:", input);
      const user = res.data.user;
      
      // Check if this is a registration flow (token has registration flag)
      if (res.data.token) {
        try {
          const payload = JSON.parse(atob(res.data.token.split('.')[1]));
          if (payload.registration) {
            user.isRegistration = true;
          }
        } catch (e) {
          console.error("Failed to decode token:", e.message);
        }
      }
      
      localStorage.setItem("user", JSON.stringify(user));
      
      // 🔑 Store token if provided (for OTP verification during registration)
      if (res.data.token) {
        localStorage.setItem("token", res.data.token);
      }
      
      navigate("/");
    } catch (err) {
      if (err.response?.status === 400) {
        // New user → role selection
        setOpenRolePopup(true);
      } else {
        alert("Login failed. Try again.");
      }
    }
  };

  const selectRole = async (role) => {
    try {
      const res = await api.post("/auth/login", {
        ...buildLoginPayload(),
        role,
      });

      // For new users, add a flag to indicate this is a registration flow
      const userData = res.data.user;
      if (res.data.isNewUser || res.data.requiresPasswordSetup) {
        userData.isRegistration = true;
      }
      
      localStorage.setItem("user", JSON.stringify(userData));
      
      // 🔑 Store token if provided (for OTP verification during registration)
      if (res.data.token) {
        localStorage.setItem("token", res.data.token);
      }
      
      setOpenRolePopup(false);

      navigate(`/register/${role}`);
    } catch (err) {
      alert("Registration failed");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        backgroundColor: "#f3f3f3",
        pt: 6,
      }}
    >
      <Paper elevation={3} sx={{ p: 2.5, width: 400, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          Sign in
        </Typography>

        <Box component="form" onSubmit={handleLogin} sx={{ width: "100%" }}>
          <TextField
            fullWidth
            label="Mobile number or Email"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            sx={{ mb: 2 }}
          />

          {/* 🔐 PASSWORD STEP (Amazon-style reveal) */}
          {step === "PASSWORD" && (
  <>
    <TextField
      fullWidth
      label="Password"
      type="password"
      value={password}
      onChange={(e) => setPassword(e.target.value)}
      sx={{ mb: 1 }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          handleLogin(e);
        }
      }}
    />

    <Typography
      sx={{
        color: "#0066c0",
        cursor: "pointer",
        fontSize: 13,
        mb: 2,
      }}
      onClick={() =>
  navigate("/forgot-password", {
    state: { username: input },
  })
}
    >
      Forgot Password?
    </Typography>
  </>
)}
          

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: "#ff9900",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "20px",
              py: 1,
              "&:hover": { backgroundColor: "#ff8800" },
            }}
          >
            {step === "PASSWORD" ? "Sign in" : "Continue"}
          </Button>
        </Box>

        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 1.2,
            color: "text.secondary",
            lineHeight: 1.4,
          }}
        >
          By continuing, you agree to our{" "}
          <Box component="span" sx={{ color: "#0066c0", cursor: "pointer" }}>
            Terms of Use
          </Box>{" "}
          and{" "}
          <Box component="span" sx={{ color: "#0066c0", cursor: "pointer" }}>
            Privacy Policy
          </Box>
          .
        </Typography>
      </Paper>

      {/* ROLE SELECTION POPUP */}
      <Dialog open={openRolePopup} onClose={() => setOpenRolePopup(false)}>
        <IconButton
          onClick={() => setOpenRolePopup(false)}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>

        <DialogTitle>Select Your Role</DialogTitle>

        <DialogContent>
          <Typography>Please choose how you want to continue:</Typography>
        </DialogContent>

        <DialogActions sx={{ flexDirection: "column", p: 2 }}>
          {["customer", "vendor", "agent"].map((r) => (
            <Button
              key={r}
              fullWidth
              variant="contained"
              onClick={() => selectRole(r)}
              sx={{
                mt: 1,
                backgroundColor: "#ff9900",
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "20px",
                "&:hover": { backgroundColor: "#ff8800" },
              }}
            >
              I am a {r}
            </Button>
          ))}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
